/**
 * LeadDetailDrawer — the ONE shared slide-in detail surface for every new
 * Lens view (AD-046 §7). A row/card/pin/chip click in List/Board/Calendar/
 * Map opens THIS drawer; it mounts:
 *   1. <LeadStageStrip> — ‹Prev/Next› legal-only hops (same write path), and
 *   2. the UNMODIFIED <LeadDetail> (which itself mounts <ChatSurface
 *      surface="lead">, Set-price, Convert, trash, the record).
 *
 * No view has its own editor and there is NO parallel write path: the strip
 * and the record both flow through the single `onChange`→`updateLead` seam
 * the Existing screen already uses. On ≤860px it becomes a full-screen
 * overlay (mobile parity, AD-046 §10).
 */
import { useEffect, useRef, useState } from 'react';
import { LeadDetail } from '../routes/Leads';
import LeadStageStrip from './LeadStageStrip';
import { transitionLead, type Lead, type LeadState } from '../lib/leads';
import type { FStage } from '../lib/leadPipeline';
import type { ClientRow, LiveClient, LivePartner, LiveProject, ClientMatch } from '../lib/projectStore';

/** Tab-trap focusable selector (visible, enabled, tabbable). */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function LeadDetailDrawer({
  lead,
  onClose,
  onChange,
  authorName,
  isTeamView,
  isAdmin,
  mayTrash,
  onTrash,
  knownClients,
  knownPartners,
  liveProjects,
  clientMatch,
  contactPool,
  canCommit,
  isMobile,
  onOpenFull,
}: {
  lead: Lead;
  onClose: () => void;
  onChange: (l: Lead) => void;
  authorName: string;
  isTeamView: boolean;
  isAdmin: boolean;
  mayTrash: boolean;
  onTrash: (l: Lead) => void;
  knownClients: ClientRow[];
  knownPartners: LivePartner[];
  liveProjects: LiveProject[];
  clientMatch: ClientMatch | null;
  contactPool: LiveClient[];
  /** false → strip is read-only (no commit rights). */
  canCommit: boolean;
  isMobile: boolean;
  /** Optional bridge to the full-page lead route (Screen 1 "Open full page").
   *  When omitted (e.g. the dev-harness) the affordance is not rendered. */
  onOpenFull?: () => void;
}) {
  const panelRef = useRef<HTMLElement>(null);
  // Latest lead id (read in the unmount cleanup to restore focus to its row/card).
  const leadIdRef = useRef(lead.id);
  leadIdRef.current = lead.id;

  // ── Desktop drag-to-resize (AD-046 §7; design system §2.7) ────────────────
  // null → use the fluid default `min(800px, 85vw)`. A number → persisted px.
  // Never applies on mobile (full-screen overlay is unchanged).
  const [widthPx, setWidthPx] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  // Live drag bookkeeping (refs avoid stale-closure reads inside move/up).
  const dragRef = useRef<{ startX: number; startWidth: number; latest: number } | null>(null);

  const MIN_W = 360;
  // Recompute the ceiling each call so a window resize can't strand the panel
  // wider than the viewport. Guarded for the node test env (no `window`).
  const maxW = () =>
    typeof window !== 'undefined' ? Math.min(1100, Math.round(window.innerWidth * 0.96)) : 1100;
  const clampW = (w: number) => Math.max(MIN_W, Math.min(maxW(), Math.round(w)));

  // Persist the chosen width (guarded; the node test env has no localStorage).
  const persistWidth = (w: number) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('avbp.leadDrawer.width', String(w));
    } catch {
      /* storage unavailable (private mode / SSR) — width simply won't persist */
    }
  };

  // Restore the persisted width on mount (guarded + clamped to the live max).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem('avbp.leadDrawer.width');
      if (raw != null) {
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n > 0) setWidthPx(clampW(n));
      }
    } catch {
      /* storage unavailable — fall back to the fluid default */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The panel's current rendered width, used as the drag/keyboard start point
  // and for aria-valuenow when no explicit width is set yet.
  const measuredWidth = () =>
    panelRef.current?.getBoundingClientRect().width ??
    widthPx ??
    (typeof window !== 'undefined' ? Math.min(800, Math.round(window.innerWidth * 0.85)) : 800);

  function onHandlePointerDown(e: React.PointerEvent) {
    if (isMobile) return;
    const startWidth = measuredWidth();
    dragRef.current = { startX: e.clientX, startWidth, latest: startWidth };
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }
  function onHandlePointerMove(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    // Panel is right-anchored: dragging the left edge leftward widens it.
    const next = clampW(d.startWidth - (e.clientX - d.startX));
    d.latest = next;
    setWidthPx(next);
  }
  function onHandlePointerUp(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDragging(false);
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    persistWidth(d.latest);
  }
  // Keyboard a11y (design system §2.7): Arrow keys nudge, Home/End jump.
  function onHandleKeyDown(e: React.KeyboardEvent) {
    const cur = widthPx ?? measuredWidth();
    let next: number | null = null;
    if (e.key === 'ArrowLeft') next = cur + 24; // wider
    else if (e.key === 'ArrowRight') next = cur - 24; // narrower
    else if (e.key === 'Home') next = maxW(); // widest
    else if (e.key === 'End') next = MIN_W; // narrowest
    if (next == null) return;
    e.preventDefault();
    const clamped = clampW(next);
    setWidthPx(clamped);
    persistWidth(clamped);
  }
  const ariaNow = Math.round(widthPx ?? measuredWidth());

  // Esc closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus management (a11y): on open, move focus into the panel and lock the
  // background (scroll lock; Tab is trapped below). On close, restore focus to
  // whatever opened the drawer (the row/card/pin the user activated).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      // Return focus to the row/card that opened the drawer. The list/board
      // re-renders on close and replaces that DOM node, so re-find it by its
      // stable data-lead-id (this cleanup runs after that re-render has
      // committed); fall back to the live opener if it is still connected.
      const id = leadIdRef.current;
      const origin =
        (id && document.querySelector<HTMLElement>(`[data-lead-id="${id}"]`)) ||
        (opener && opener.isConnected ? opener : null);
      origin?.focus?.();
    };
  }, []);

  // Trap Tab within the panel so the keyboard can't reach the locked backdrop.
  function trapTab(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const node = panelRef.current;
    if (!node) return;
    const items = Array.from(
      node.querySelectorAll<HTMLElement>(FOCUSABLE),
    ).filter((el) => el.offsetParent !== null);
    if (items.length === 0) {
      e.preventDefault();
      node.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === node) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const nowISO = () => new Date().toISOString();

  // Strip → SAME guarded transition the Existing screen uses. Illegal hops
  // are never offered by the strip, but transitionLead also throws on one,
  // so a programmatic illegal call is a no-op write (defensive).
  function handleState(to: LeadState) {
    try {
      onChange(transitionLead(lead, to, nowISO()));
    } catch {
      /* illegal — ignored (strip never offers one) */
    }
  }
  // Fiverr advance → snapshot-field write (persisted by the parent onChange).
  function handleFstage(to: FStage) {
    onChange({ ...lead, fstage: to, updatedAt: nowISO() });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Lead — ${lead.name}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        justifyContent: isMobile ? 'stretch' : 'flex-end',
      }}
    >
      {/* scrim */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(20,20,30,.32)' }}
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={trapTab}
        style={{
          position: 'relative',
          width: isMobile ? '100%' : widthPx != null ? `${widthPx}px` : 'min(800px, 85vw)',
          height: '100%',
          background: 'var(--paper, #fff)',
          borderLeft: isMobile ? 'none' : '1px solid var(--rule, #E5E7EB)',
          // Lift to a clearer float while dragging (§2.7: --e-1 rest, --e-2 drag).
          boxShadow: dragging
            ? 'var(--e-2, -4px 0 34px rgba(20,20,30,.22))'
            : 'var(--e-1, -2px 0 22px rgba(20,20,30,.14))',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        {/* Left-edge resize handle — desktop only (mobile is a full-screen
            overlay). Right-anchored panel, so dragging left widens it. It is a
            focusable child, so it participates in the existing Tab trap. */}
        {!isMobile && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            aria-valuemin={MIN_W}
            aria-valuemax={maxW()}
            aria-valuenow={ariaNow}
            tabIndex={0}
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onKeyDown={onHandleKeyDown}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 8,
              cursor: 'col-resize',
              touchAction: 'none',
              zIndex: 2,
              // Reveal the accent-soft grab affordance on hover/active (§2.7);
              // transparent at rest.
              background: dragging ? 'var(--accent-soft, #7AD0C0)' : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!dragging) e.currentTarget.style.background = 'var(--accent-soft, #7AD0C0)';
            }}
            onMouseLeave={(e) => {
              if (!dragging) e.currentTarget.style.background = 'transparent';
            }}
          />
        )}
        <header
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '10px 14px',
            borderBottom: '1px solid var(--rule, #E5E7EB)',
            flex: '0 0 auto',
          }}
        >
          {/* top row: breadcrumb · Open full page · close (Screen 1 .topline) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                color: 'var(--ink-mute, #6B7280)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ color: 'var(--ink-soft, #374151)', fontWeight: 600 }}>Leads</span>
              <span style={{ margin: '0 5px', color: 'var(--rule-strong, #D1D5DB)' }}>›</span>
              {lead.name}
            </div>
            {onOpenFull && (
              <button
                type="button"
                onClick={onOpenFull}
                aria-label="Open full page"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  flex: '0 0 auto',
                  fontSize: 12.5,
                  fontWeight: 650,
                  cursor: 'pointer',
                  color: 'var(--accent-deep, #075E54)',
                  background: 'var(--accent-wash, #E9F7EF)',
                  border: '1px solid var(--accent-line, #CDEBDD)',
                  borderRadius: 'var(--r-sm, 8px)',
                  padding: '6px 11px',
                  lineHeight: 1,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden="true"
                >
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
                Open full page
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                border: '1px solid var(--rule-strong, #D1D5DB)',
                background: 'var(--paper, #fff)',
                borderRadius: 'var(--r-pill, 999px)',
                width: 30,
                height: 30,
                cursor: 'pointer',
                fontSize: 15,
                lineHeight: 1,
                flex: '0 0 auto',
              }}
            >
              ✕
            </button>
          </div>

          {/* identity block (Screen 1 .identity) — name + id / consultation line */}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {lead.name}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--ink-mute, #6B7280)' }}>
              {lead.id}
              {lead.consultationPaid && (
                <span style={{ marginLeft: 6, color: 'var(--info, #2563EB)', fontWeight: 700 }}>
                  · ₹599 consultation paid
                </span>
              )}
            </div>
          </div>
        </header>

        {/* the shared stage strip */}
        <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--rule, #E5E7EB)', flex: '0 0 auto', overflowX: 'auto' }}>
          <LeadStageStrip
            lead={lead}
            onState={handleState}
            onFstage={handleFstage}
            disabled={!canCommit}
          />
        </div>

        {/* the UNMODIFIED record. `key={lead.id}` (owner bug 2026-07-03):
            without it, switching leads kept ONE LeadDetail instance alive, so
            its per-lead form drafts (the admin identity Name/Phone/Email,
            concern, sqft, convert pickers) stayed seeded from the PREVIOUS
            lead — typing a name then APPENDED onto the stale value and Save
            wrote the concatenation. The Existing screen already remounts per
            lead (key={selected.id}); this restores the same contract here. */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '4px 14px 14px', scrollbarGutter: 'stable' }}>
          <LeadDetail
            key={lead.id}
            lead={lead}
            onChange={onChange}
            authorName={authorName}
            isTeamView={isTeamView}
            isAdmin={isAdmin}
            mayTrash={mayTrash}
            onTrash={onTrash}
            knownClients={knownClients}
            knownPartners={knownPartners}
            liveProjects={liveProjects}
            clientMatch={clientMatch}
            contactPool={contactPool}
          />
        </div>
      </aside>
    </div>
  );
}
